import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { QrCode, RefreshCw, FileDown, Image as ImageIcon, Lock, Unlock, RotateCcw } from "lucide-react";
import { useApi } from "../hooks/useApi";
import api, { formatError } from "../lib/api";
import { Button } from "./ui/button";
import { fmtDateTime } from "../lib/dates";

const err = (e) => toast.error(formatError(e.response?.data?.detail) || "Something went wrong");
const OVERRIDE_MSG = { auto: "Back to automatic window", open: "Check-in opened", close: "Check-in closed" };

async function download(path, filename) {
  const r = await api.get(path, { responseType: "blob" });
  const href = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = href; a.download = filename; a.click(); URL.revokeObjectURL(href);
}

function useCheckIn(eventId, onChanged) {
  const { data: s, reload } = useApi(`/admin/meetings/${eventId}/checkin`);
  const [busy, setBusy] = useState(false);
  useEffect(() => { const t = setInterval(reload, 15000); return () => clearInterval(t); }, [reload]);
  const act = async (fn, msg) => { setBusy(true); try { await fn(); toast.success(msg); reload(); onChanged?.(); } catch (e) { err(e); } finally { setBusy(false); } };
  const generate = () => act(() => api.post(`/admin/meetings/${eventId}/checkin/generate`), s?.has_code ? "New code generated — old printouts no longer work" : "Check-in code ready");
  const override = (action, minutes) => act(() => api.post(`/admin/meetings/${eventId}/checkin/override`, { action, minutes }), OVERRIDE_MSG[action]);
  return { s, busy, generate, override };
}

function WindowBadge({ s }) {
  const cls = s.open ? "bg-green-100 text-green-800" : "bg-stone-200 text-stone-700";
  return <span className={`rounded-full px-3 py-1 text-xs font-mono-accent ${cls}`} data-testid="checkin-window-badge">{s.open ? "Check-in OPEN" : "Check-in closed"} · {s.reason}</span>;
}

function WindowControls({ s, disabled, override }) {
  return (
    <div className="flex flex-wrap gap-2 border-t border-gold/15 pt-2">
      {s.override !== "open" && <Button size="sm" variant="outline" onClick={() => override("open", 60)} disabled={disabled} data-testid="checkin-open"><Unlock size={14} className="mr-1" /> Open now (1 h)</Button>}
      {s.open && <Button size="sm" variant="outline" onClick={() => override("close")} disabled={disabled} data-testid="checkin-close"><Lock size={14} className="mr-1" /> Close check-in</Button>}
      {s.override && <Button size="sm" variant="ghost" onClick={() => override("auto")} disabled={disabled} data-testid="checkin-auto"><RotateCcw size={14} className="mr-1" /> Back to automatic</Button>}
    </div>
  );
}

function CodeDetails({ s, eventId, eventDate, disabled, generate, override }) {
  return (
    <div className="mt-4 flex flex-wrap items-start gap-4">
      <img src={`${process.env.REACT_APP_BACKEND_URL}/api/admin/meetings/${eventId}/checkin/qr.png?v=${s.generated_at}`} alt="Check-in QR code" className="h-36 w-36 rounded-lg border border-gold/30 bg-white p-1" data-testid="checkin-qr-image" />
      <div className="flex-1 space-y-2 text-sm">
        <p className="text-xs text-muted-foreground">Generated {fmtDateTime(s.generated_at)} by {s.generated_by}</p>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => download(`/admin/meetings/${eventId}/checkin/poster.pdf`, `checkin-${eventDate}.pdf`).catch(err)} data-testid="checkin-poster-pdf"><FileDown size={14} className="mr-1" /> Poster (PDF)</Button>
          <Button size="sm" variant="outline" onClick={() => download(`/admin/meetings/${eventId}/checkin/qr.png`, `checkin-${eventDate}.png`).catch(err)} data-testid="checkin-qr-download"><ImageIcon size={14} className="mr-1" /> QR image (PNG)</Button>
          <Button size="sm" variant="ghost" onClick={generate} disabled={disabled} className="text-crimson" data-testid="checkin-regenerate"><RefreshCw size={14} className="mr-1" /> Regenerate</Button>
        </div>
        <WindowControls s={s} disabled={disabled} override={override} />
        {s.override === "open" && <p className="text-xs text-green-800">Manually open until {fmtDateTime(s.override_until)}.</p>}
      </div>
    </div>
  );
}

export function CheckInQR({ eventId, eventDate, closed, onChanged }) {
  const { s, busy, generate, override } = useCheckIn(eventId, onChanged);
  if (!s?.supported) return null;
  const disabled = busy || closed;
  const windowText = s.opens_at ? `from ${fmtDateTime(s.opens_at)} to ${fmtDateTime(s.closes_at)}` : "during the meeting";
  return (
    <div className="glass rounded-2xl p-6" data-testid="checkin-qr-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-serif-display flex items-center gap-2 text-xl font-bold text-crimson"><QrCode size={20} className="text-gold" /> QR self check-in</h3>
        <WindowBadge s={s} />
      </div>
      <p className="mt-1 text-xs text-muted-foreground">Members scan, sign in and tap "I'm here". Works {windowText} (Boston time) unless you open or close it below. Self check-ins so far: <strong data-testid="checkin-self-count">{s.self_checkins}</strong>.</p>
      {s.has_code
        ? <CodeDetails s={s} eventId={eventId} eventDate={eventDate} disabled={disabled} generate={generate} override={override} />
        : <Button onClick={generate} disabled={disabled} className="mt-4 bg-crimson hover:bg-crimson-hover" data-testid="checkin-generate"><QrCode size={16} className="mr-1" /> Generate check-in QR</Button>}
    </div>
  );
}
